import Box from "@cloudscape-design/components/box"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import FormField from "@cloudscape-design/components/form-field"
import Input from "@cloudscape-design/components/input"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Textarea from "@cloudscape-design/components/textarea"
import { useState, useEffect } from "react"
import { UseFormRegister, UseFormSetValue, FieldErrors } from "react-hook-form"

import { ReadOnlyValue } from "../shared/ReadOnlyValue"
import { DQDL_RULES } from "./constants"
import { FieldMultiSelect } from "./FieldMultiSelect"
import { FieldSelect } from "./FieldSelect"
import { isFiniteNumeric } from "./threshold"
import { DqdlRule, DataQualityFormData } from "./types"
import { MetadataField } from "./useMetadataFields"

/**
 * Props for the DqdlRuleEditor component.
 */
interface DqdlRuleEditorProps {
  /**
   * The DQDL rule being edited.
   */
  rule: DqdlRule

  /**
   * Index of this rule in the parent array.
   */
  index: number

  /**
   * Which rule array this belongs to ('majorRules' or 'minorRules')
   */
  prefix: "majorRules" | "minorRules"

  /**
   * React-hook-form register function for binding inputs.
   */
  register: UseFormRegister<DataQualityFormData>

  /**
   * React-hook-form setValue function for programmatic updates.
   */
  setValue: UseFormSetValue<DataQualityFormData>

  /**
   * Whether the form is in edit mode.
   */
  canEdit: boolean

  /**
   * Form validation errors.
   */
  errors?: FieldErrors<DataQualityFormData>

  /**
   * Available fields from metadata for dropdowns.
   */
  availableFields: MetadataField[]

  /**
   * Callback to navigate to the Metadata tab.
   */
  onNavigateToMetadata?: () => void
}

/**
 * Converts a stored decimal threshold (e.g. "0.95") to a percentage display value (e.g. "95").
 */
function thresholdToDisplay(v: string | null | undefined): string {
  if (!v) return ""
  const num = parseFloat(v)
  if (isNaN(num)) return ""
  const pct = num * 100
  return String(Math.round(pct * 1e10) / 1e10)
}

/**
 * Controlled number input that displays a decimal 0-1 threshold as a percentage (0-100) and
 * converts user input back to decimal on blur.
 */
function ThresholdPercentInput({
  value,
  onChange,
  canEdit,
  error,
}: {
  value: string | null | undefined
  onChange: (decimal: string) => void
  canEdit: boolean
  error?: string
}) {
  const [display, setDisplay] = useState(() => thresholdToDisplay(value))

  useEffect(() => {
    setDisplay(thresholdToDisplay(value))
  }, [value])

  const commitValue = () => {
    if (!display) {
      onChange("")
      return
    }
    if (!isFiniteNumeric(display)) {
      // Commit invalid input (don't discard) so schema validation can surface the error.
      onChange(display)
      return
    }
    const decimal = Number(display) / 100
    onChange(String(Math.round(decimal * 1e10) / 1e10))
  }

  return (
    <FormField
      label="Threshold (%)"
      constraintText={error ? undefined : "Percentage of rows that must be non-null (0-100)"}
      errorText={error}
    >
      {canEdit ? (
        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
          <Input
            type="number"
            inputMode="decimal"
            step="any"
            placeholder="95"
            value={display}
            onChange={({ detail }) => setDisplay(detail.value)}
            onBlur={commitValue}
            ariaLabel="threshold"
          />
          <Box variant="span" color="text-body-secondary">
            %
          </Box>
        </SpaceBetween>
      ) : (
        <ReadOnlyValue value={display ? `${display}%` : ""} />
      )}
    </FormField>
  )
}

/**
 * Editor component for a single DQDL validation rule. Renders type-specific input fields based on
 * the rule type. Uses FieldSelect/FieldMultiSelect for column fields (except CustomSql).
 */
export const DqdlRuleEditor: React.FC<DqdlRuleEditorProps> = ({
  rule,
  index,
  prefix,
  setValue,
  canEdit,
  errors,
  availableFields,
  onNavigateToMetadata,
}) => {
  const fields = DQDL_RULES[rule.type].fields
  const ruleErrors = errors?.[prefix]?.[index] as Record<string, { message?: string }> | undefined

  const handleColumnChange = (value: string) => {
    setValue(`${prefix}.${index}.column`, value, { shouldDirty: true })
  }

  const handleColumnsChange = (values: string[]) => {
    setValue(`${prefix}.${index}.columns`, values, { shouldDirty: true })
  }

  return (
    <Box padding={{ top: "s" }}>
      <SpaceBetween size="m">
        {/* Full-width multi-column selector (IsPrimaryKey) */}
        {fields.includes("columns") && (
          <FormField
            label="Columns"
            constraintText={
              ruleErrors?.columns ? undefined : "Combination of columns that must be unique"
            }
            errorText={ruleErrors?.columns?.message}
          >
            <FieldMultiSelect
              value={rule.columns || []}
              onChange={handleColumnsChange}
              availableFields={availableFields}
              readOnly={!canEdit}
              placeholder="Add column"
              hasError={!!ruleErrors?.columns}
              onNavigateToMetadata={onNavigateToMetadata}
            />
          </FormField>
        )}

        {/* Paired narrow fields */}
        <ColumnLayout columns={2} borders="none">
          {fields.includes("column") && (
            <FormField
              label="Column"
              constraintText={ruleErrors?.column ? undefined : "Column to validate"}
              errorText={ruleErrors?.column?.message}
            >
              <FieldSelect
                value={rule.column}
                onChange={handleColumnChange}
                availableFields={availableFields}
                readOnly={!canEdit}
                placeholder="Select column"
                hasError={!!ruleErrors?.column}
                onNavigateToMetadata={onNavigateToMetadata}
              />
            </FormField>
          )}

          {fields.includes("pattern") && (
            <FormField
              label="Pattern"
              constraintText={
                ruleErrors?.pattern ? undefined : "Regex pattern the column value must match"
              }
              errorText={ruleErrors?.pattern?.message}
            >
              {canEdit ? (
                <Input
                  type="text"
                  placeholder="^\d{4}-\d{2}-\d{2}$"
                  value={rule.pattern ?? ""}
                  onChange={({ detail }) =>
                    setValue(`${prefix}.${index}.pattern`, detail.value, { shouldDirty: true })
                  }
                  invalid={!!ruleErrors?.pattern}
                />
              ) : (
                <ReadOnlyValue value={rule.pattern} />
              )}
            </FormField>
          )}

          {fields.includes("threshold") && (
            <ThresholdPercentInput
              value={rule.threshold}
              onChange={(decimal) =>
                setValue(`${prefix}.${index}.threshold`, decimal, { shouldDirty: true })
              }
              canEdit={canEdit}
              error={ruleErrors?.threshold?.message}
            />
          )}

          {fields.includes("days") && (
            <FormField
              label="Days"
              constraintText={
                ruleErrors?.days ? undefined : "Data must be within this many days old"
              }
              errorText={ruleErrors?.days?.message}
            >
              {canEdit ? (
                <Input
                  type="number"
                  placeholder="30"
                  value={rule.days != null ? String(rule.days) : ""}
                  onChange={({ detail }) =>
                    setValue(
                      `${prefix}.${index}.days`,
                      detail.value === "" ? undefined : Number(detail.value),
                      { shouldDirty: true },
                    )
                  }
                  invalid={!!ruleErrors?.days}
                />
              ) : (
                <ReadOnlyValue value={rule.days} />
              )}
            </FormField>
          )}

          {fields.includes("min") && (
            <FormField label="Min" constraintText="Minimum allowed value">
              {canEdit ? (
                <Input
                  type="number"
                  placeholder="1"
                  value={rule.min != null ? String(rule.min) : ""}
                  onChange={({ detail }) =>
                    setValue(
                      `${prefix}.${index}.min`,
                      detail.value === "" ? undefined : Number(detail.value),
                      { shouldDirty: true },
                    )
                  }
                />
              ) : (
                <ReadOnlyValue value={rule.min} />
              )}
            </FormField>
          )}

          {fields.includes("max") && (
            <FormField label="Max" constraintText="Maximum allowed value">
              {canEdit ? (
                <Input
                  type="number"
                  placeholder="50"
                  value={rule.max != null ? String(rule.max) : ""}
                  onChange={({ detail }) =>
                    setValue(
                      `${prefix}.${index}.max`,
                      detail.value === "" ? undefined : Number(detail.value),
                      { shouldDirty: true },
                    )
                  }
                />
              ) : (
                <ReadOnlyValue value={rule.max} />
              )}
            </FormField>
          )}
        </ColumnLayout>

        {/* Full-width SQL editor (CustomSql) */}
        {fields.includes("sql") && (
          <FormField
            label="SQL"
            constraintText={
              ruleErrors?.sql ? undefined : "Query that returns rows failing the check"
            }
            errorText={ruleErrors?.sql?.message}
          >
            {canEdit ? (
              <Textarea
                placeholder="SELECT * FROM primary WHERE ..."
                value={rule.sql ?? ""}
                onChange={({ detail }) =>
                  setValue(`${prefix}.${index}.sql`, detail.value, { shouldDirty: true })
                }
                rows={2}
                invalid={!!ruleErrors?.sql}
              />
            ) : (
              <ReadOnlyValue value={rule.sql} />
            )}
          </FormField>
        )}

        {/* Full-width optional WHERE clause */}
        {fields.includes("where") && (
          <FormField
            label="Where Clause (optional)"
            constraintText="Only apply this rule to rows matching this condition"
          >
            {canEdit ? (
              <Input
                type="text"
                placeholder="AECAT = 'SERIOUS'"
                value={rule.where ?? ""}
                onChange={({ detail }) =>
                  setValue(`${prefix}.${index}.where`, detail.value, { shouldDirty: true })
                }
              />
            ) : (
              <ReadOnlyValue value={rule.where} />
            )}
          </FormField>
        )}
      </SpaceBetween>
    </Box>
  )
}
